import * as React from 'react';
import { Origin, EditorType } from '../manage-installed-apps-page';
import { DeleteModal } from './delete-modal/delete-modal';
import { CheckAppStatusModal } from './check-app-status-modal/check-app-status-modal';
import { ReviewAppModal } from '@wix/app-market-collection-widget';
import { SupportModal } from './support-modal/support-modal';
import { UpdateModal } from './update-modal/update-modal';
import NewAppSettingsModal from './app-settings-modal/new-app-settings-modal';
import { ModuleRegistry } from 'react-module-container';
import type { ManagedApp } from '@wix/ambassador-devcenter-managedapps-v1-managed-app/types';

export enum ModalNames {
  NONE = 'none',
  DELETE = 'delete',
  IN_CONTEXT_INSTALLATION = 'inContextInstallation',
  APP_STATUS = 'checkAppStatus',
  APP_SETTINGS = 'appSettings',
  RATE_APP = 'rateApp',
  SUPPORT = 'support',
  EDIT_REVIEW = 'editReview',
  UPDATE = 'update',
}

export interface IModalProps {
  openModalName?: ModalNames;
  closeModal?(): void;
  app?: ManagedApp;
  baseUrl?: string;
  manageAppsStaticsUrl?: string;
  authorizationHeader?: string;
  origin?: Origin;
  editorUrl?: string;
  checkStatusUrl?: string;
  settingsUrl?: string;
  locale?: string;
  msId?: string;
  deleteApp?(app: ManagedApp, showNotification: boolean): void;
  showUserActionNotification?(options: any): void;
  editorType?: string;
  openDeleteModalAppInEditor?(appId: string): void;
  openCompleteUpdateInEditor?(appId: string): void;
}

export function Modals({
  openModalName,
  closeModal,
  app,
  baseUrl,
  authorizationHeader,
  origin,
  editorUrl,
  checkStatusUrl,
  settingsUrl,
  deleteApp,
  locale,
  msId,
  showUserActionNotification,
  editorType,
  openDeleteModalAppInEditor,
}: IModalProps) {
  const shouldUseNewReviewAppModal =
    openModalName === ModalNames.RATE_APP ||
    openModalName === ModalNames.EDIT_REVIEW;

  const showSuccessToast = (message: string) => {
    if (showUserActionNotification) {
      showUserActionNotification({
        message,
        biName: 'review-app',
        type: 'SUCCESS',
      });
    }
  };

  return (
    <>
      {openModalName === ModalNames.DELETE && (
        <DeleteModal
          app={app}
          msId={msId}
          origin={origin}
          editorUrl={editorUrl}
          deleteApp={deleteApp}
          closeModal={closeModal}
          editorType={editorType}
          baseUrl={baseUrl}
          authorizationHeader={authorizationHeader}
          openDeleteModalAppInEditor={openDeleteModalAppInEditor}
        />
      )}
      {openModalName === ModalNames.APP_STATUS && (
        <CheckAppStatusModal
          checkStatusUrl={checkStatusUrl}
          closeModal={closeModal}
        />
      )}
      {openModalName === ModalNames.APP_SETTINGS && (
        <NewAppSettingsModal
          settingsUrl={settingsUrl}
          closeModal={closeModal}
        />
      )}
      {shouldUseNewReviewAppModal && (
        <ReviewAppModal
          locale={locale}
          appDefId={app.appId}
          isOpen={true}
          closeFn={closeModal}
          showSuccessToast={showSuccessToast}
          additionalBiData={{
            msid: msId,
            tagType: 'manage_apps',
            platform:
              origin === Origin.BIZMGR
                ? editorType === EditorType.ADI
                  ? 'adi_dashboard'
                  : 'dashboard'
                : origin,
          }}
        />
      )}
      {openModalName === ModalNames.SUPPORT && (
        <SupportModal
          app={app}
          msId={msId}
          origin={origin}
          closeModal={closeModal}
          showUserActionNotification={showUserActionNotification}
          authorizationHeader={authorizationHeader}
          baseUrl={baseUrl}
          editorType={editorType}
        />
      )}
    </>
  );
}

export function ModalsNew({
  openModalName,
  closeModal,
  app,
  baseUrl,
  authorizationHeader,
  origin,
  editorUrl,
  checkStatusUrl,
  settingsUrl,
  deleteApp,
  locale,
  msId,
  showUserActionNotification,
  editorType,
  openDeleteModalAppInEditor,
  openCompleteUpdateInEditor,
}: IModalProps) {
  const InContextInstallationModal = ModuleRegistry.component(
    'in-context-installation-modal',
  );

  const shouldUseNewReviewAppModal =
    openModalName === ModalNames.RATE_APP ||
    openModalName === ModalNames.EDIT_REVIEW;

  const showSuccessToast = (message: string) => {
    if (showUserActionNotification) {
      showUserActionNotification({
        message,
        biName: 'review-app',
        type: 'SUCCESS',
      });
    }
  };
  return (
    <>
      {openModalName === ModalNames.UPDATE && (
        <UpdateModal
          app={app}
          msId={msId}
          closeModal={closeModal}
          openCompleteUpdateInEditor={openCompleteUpdateInEditor}
        />
      )}
      {openModalName === ModalNames.DELETE && (
        <DeleteModal
          app={app}
          msId={msId}
          origin={origin}
          editorUrl={editorUrl}
          deleteApp={deleteApp}
          closeModal={closeModal}
          editorType={editorType}
          baseUrl={baseUrl}
          authorizationHeader={authorizationHeader}
          openDeleteModalAppInEditor={openDeleteModalAppInEditor}
        />
      )}
      {openModalName === ModalNames.IN_CONTEXT_INSTALLATION && (
        <InContextInstallationModal
          appId={(app as unknown as ManagedApp)?.appId}
          isOpen={true}
          intent="dayful-flow"
          onInstallationSuccess={closeModal}
          onInstallationCancel={closeModal}
        />
      )}
      {openModalName === ModalNames.APP_STATUS && (
        <CheckAppStatusModal
          checkStatusUrl={checkStatusUrl}
          closeModal={closeModal}
        />
      )}
      {openModalName === ModalNames.APP_SETTINGS && (
        <NewAppSettingsModal
          settingsUrl={settingsUrl}
          closeModal={closeModal}
        />
      )}
      {shouldUseNewReviewAppModal && (
        <ReviewAppModal
          locale={locale}
          appDefId={(app as unknown as ManagedApp)?.appId}
          isOpen={true}
          closeFn={closeModal}
          showSuccessToast={showSuccessToast}
          additionalBiData={{
            msid: msId,
            tagType: 'manage_apps',
            platform:
              origin === Origin.BIZMGR
                ? editorType === EditorType.ADI
                  ? 'adi_dashboard'
                  : 'dashboard'
                : origin,
          }}
        />
      )}
      {openModalName === ModalNames.SUPPORT && (
        <SupportModal
          app={app}
          msId={msId}
          origin={origin}
          closeModal={closeModal}
          showUserActionNotification={showUserActionNotification}
          authorizationHeader={authorizationHeader}
          baseUrl={baseUrl}
          editorType={editorType}
        />
      )}
    </>
  );
}
